// ListLoadingFrame.tsx · v12.10.21 (#1945 the logo frame that replaces the yellow "Loading the rest of the list… 50 of 999+ calls" bar; #1944 the soft grey loading mark of the counters) · 2026-10-26 · VoiceSpectrum AI
//
// LOCKED prototype UI-PROTO-MESSAGES-LOADING v1.5 (Tushar 10/26): the logo's top bracket (light indigo #c7d2fe, with the notch)
// and bottom bracket (light violet #ddd6fe) frame one compact line (~36 px) under the filter line: "Loading the rest of the
// list", then dot - stub - the logo's waveform - stub - dot, the bars #a5b4fc -> #c4b5fd, animated. No numbers, no dots of
// text. Pure CSS animation (agent-workspace.css .vsload / .vsf-*); shown only while the rest of the list loads - the page
// stops rendering it when the list is complete. The bar heights / colours / delays are the prototype's, computed once here.
import type { CSSProperties } from 'react';

const BAR_COUNT = 110;

function mix(a: string, b: string, t: number): string {
  const p = (x: string) => [parseInt(x.slice(1, 3), 16), parseInt(x.slice(3, 5), 16), parseInt(x.slice(5, 7), 16)];
  const A = p(a);
  const B = p(b);
  return `rgb(${A.map((v, i) => Math.round(v + (B[i] - v) * t)).join(',')})`;
}

/** The prototype's bars: an indigo -> violet gradient, a wave-shaped height envelope, a running delay (left to right). */
const BARS: CSSProperties[] = Array.from({ length: BAR_COUNT }, (_, i) => {
  const env = 0.3 + 0.7 * Math.abs(Math.sin(i * 0.33)) * (0.6 + 0.4 * Math.sin(i * 0.09));
  return {
    background: mix('#a5b4fc', '#c4b5fd', i / (BAR_COUNT - 1)),
    ['--h' as string]: `${(env * 100).toFixed(0)}%`,
    animationDelay: `${(-(BAR_COUNT - i) * 0.05).toFixed(2)}s`,
  } as CSSProperties;
});

function Notch({ bottom }: { bottom?: boolean }) {
  return (
    <svg className="vsn" viewBox="0 0 20 7" width="20" height="7" aria-hidden="true">
      <path d={bottom ? 'M0 6 H7 L10 1.5 L13 6 H20' : 'M0 1 H7 L10 5.5 L13 1 H20'} fill="none" stroke={bottom ? '#ddd6fe' : '#c7d2fe'}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** v12.10.21 (#1945): the compact logo frame - "Loading the rest of the list" with the running waveform. */
export function ListLoadingFrame({ text = 'Loading the rest of the list' }: { text?: string }) {
  return (
    <div className="vsload" role="status" aria-live="polite" aria-label={text}>
      <div className="vsf-edge top" aria-hidden="true"><span className="c l" /><span className="ln" /><Notch /><span className="ln" /><span className="c r" /></div>
      <div className="vsf-mid">
        <span className="vsf-txt">{text}</span>
        <span className="vsf-dot" aria-hidden="true" />
        <span className="vsf-stub" aria-hidden="true" />
        <span className="vsf-wave" aria-hidden="true">{BARS.map((st, i) => <i key={i} style={st} />)}</span>
        <span className="vsf-stub v" aria-hidden="true" />
        <span className="vsf-dot v" aria-hidden="true" />
      </div>
      <div className="vsf-edge bot" aria-hidden="true"><span className="c l" /><span className="ln" /><Notch bottom /><span className="ln" /><span className="c r" /></div>
    </div>
  );
}

/** v12.10.21 (#1944): the soft grey loading mark a counter shows while the list is still loading - never 0, never a partial number. */
export function LoadingMark({ label = 'Loading' }: { label?: string }) {
  return <span className="ldm" role="img" aria-label={label} />;
}
